<template>
  <div :id="elementId">
    <v-autocomplete
      :menu-props="{ contentClass: `${elementId}-menu` }"
      color="black"
      item-color="primary"
      v-model="chosenUser"
      :label="label"
      @change="userChosen"
      :click:clear="userChosen"
      :loading="loading"
      :items="items"
      :rules="rules"
      :hint="hint"
      :persistent-hint="hint !== ''"
      item-text="name"
      :clearable="clearable && !readonly"
      return-object
      :multiple="multiple"
      :chips="chips"
      :small-chips="smallChips"
      :deletable-chips="deletableChips"
      :disabled="disabled"
      :filter="(item, queryText, itemText) => itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1"
      :readonly="readonly"
      :append-icon="readonly ? '' : '$dropdown'"
    >
      <template #append-outer v-if="!readonly">
        <v-icon @click="refreshUsers" color="primary" tabindex="-1">mdi-refresh</v-icon>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { localeCompareAlphanumeric } from '@/utils/base'

export default {
  name: 'UserAutocomplete',

  props: {
    propUserId: { type: Number },
    propUserIds: { type: Array, default: () => [] },
    propTeam: { type: Object, default: undefined },
    elementId: { type: String, required: false, default: undefined },
    label: { type: String, default: 'User' },
    fetchUsers: { type: Boolean, default: true },
    multiple: { type: Boolean, default: false },
    chips: { type: Boolean, default: false },
    smallChips: { type: Boolean, default: false },
    deletableChips: { type: Boolean, default: false },
    rules: { type: Array, default: () => [] },
    hint: { type: String, default: '' },
    allUserIds: { type: Array, required: false, default: undefined },
    disabled: { type: Boolean, default: false },
    includePreexistingUser: { type: Boolean, default: true },
    requiredClaim: { type: Object, default: undefined },
    clear: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    readonly: { type: Boolean, default: false }
  },

  watch: {
    propTeam (team) {
      if (team?.users && this.multiple) {
        this.chosenUser = team.users
      } else if (!team && this.multiple) {
        this.chosenUser = []
      }
      this.$emit('user-chosen', this.chosenUser)
      this.setItems()
    },

    allUserIds () {
      this.setItems()
    },

    clear (v) {
      if (v) this.chosenUser = undefined
    },

    propUserId (userId) {
      if (userId == null) return
      this.chosenUser = this.allUsers.find(u => u.applicationUserId === userId)
      this.userChosen()
    }
  },

  data: () => ({
    chosenUser: undefined,
    items: [],
    loading: false,
    fetchedUsers: undefined
  }),

  async created () {
    if (this.fetchUsers) await this.getUsers()
    this.setItems()
    if (this.propUserId) {
      this.chosenUser = this.allUsers.find(u => u.applicationUserId === this.propUserId)
    } else if (this.propUserIds) {
      this.chosenUser = this.propUserIds.map(uid => this.activeUsers.find(u => u.applicationUserId === uid))
    } else if (this.propTeam) {
      this.chosenUser = this.propTeam.users
    }
    this.sortChosenUsers()
  },

  computed: {
    ...mapGetters('user', ['activeUsers', 'allUsers'])
  },

  methods: {
    ...mapActions('user', ['getActiveUsers', 'getAllUsers']),

    async refreshUsers () {
      this.loading = true
      await this.getUsers()
      this.setItems()
      this.loading = false
    },

    userChosen () {
      this.sortChosenUsers()
      this.$emit('user-chosen', this.chosenUser)
    },

    sortChosenUsers () {
      if (Array.isArray(this.chosenUser)) {
        this.chosenUser = this.chosenUser.sort((a, b) => localeCompareAlphanumeric(a.name, b.name))
      }
    },

    setItems () {
      const unsorted = this.allUserIds
        ? this.allUserIds.map(id => this.activeUsers.find(u => u.applicationUserId === id))
        : this.propTeam?.users
          ? this.propTeam.users
          : this.activeUsers

      if (this.includePreexistingUser) {
        unsorted.push(this.allUsers.find(u => u.applicationUserId === this.propUserId))
      }

      this.items = unsorted.sort((a, b) => localeCompareAlphanumeric(a.name, b.name))

      if (this.chosenUser?.applicationUserId && !this.items.some(i => this.chosenUser.applicationUserId === i.applicationUserId)) {
        this.chosenUser = undefined
        this.userChosen()
      }
    },

    async getUsers () {
      if (this.includePreexistingUser && this.activeUsers.every(u => u?.applicationUserId !== this.propUserId)) {
        await this.getAllUsers(this.requiredClaim) // Preexisting user may be inactive. Fetch all users if preexisting user is not active
      } else {
        await this.getActiveUsers(this.requiredClaim)
      }
    }
  }
}
</script>
